// react
import React from 'react';

// tanstack
import {
  Link,
  useMatches,
  useMatchRoute,
  useParams,
} from '@tanstack/react-router';

// material ui icons
import ChevronRightRoundedIcon from '@mui/icons-material/ChevronRightRounded';
import ExpandMoreRoundedIcon from '@mui/icons-material/ExpandMoreRounded';

// material ui
import {
  alpha,
  Collapse,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Tooltip,
} from '@mui/material';

// relative
import type { NavItem as NavItemType } from './navigationTypes';

interface NavItemProps {
  item: NavItemType;
  depth?: number;
  drawerOpen?: boolean;
  isExpanded: (item: NavItemType) => boolean;
  onToggle: (itemId: string, currentExpandedState: boolean) => void;
  onPopperToggle: (
    event: React.MouseEvent<HTMLElement>,
    children: NavItemType[],
  ) => void;
}

export const NavItem: React.FC<NavItemProps> = ({
  item,
  depth = 0,
  drawerOpen = true,
  isExpanded,
  onToggle,
  onPopperToggle,
}) => {
  // This is here exclusively to make sure isactive is beeing recalculated when the route changes. Otherwise, the navitem will not update its active state when the route changes.
  const matches = useMatches();
  const matchRoute = useMatchRoute();
  const { accountId, energyGroupId } = useParams({ strict: false });
  const hasChildren = item.children && item.children.length > 0;

  const hasActiveDescendant = (navItem: NavItemType): boolean => {
    if (!navItem.children?.length || !matches.length) return false;
    return navItem.children.some((child) => {
      if (
        child.path &&
        Boolean(
          matchRoute({
            to: child.path,
            params: { accountId },
            includeSearch: true,
            fuzzy: true,
          }),
        )
      )
        return true;
      return hasActiveDescendant(child);
    });
  };
  const isActive = Boolean(
    (matches &&
      item.path &&
      matchRoute({ to: item.path, params: { accountId, energyGroupId } })) ||
    (hasChildren && hasActiveDescendant(item)),
  );
  const expanded = isExpanded(item);

  // Collapsed drawer with nested items - show popper on click
  if (!drawerOpen && hasChildren) {
    return (
      <Tooltip title={item.label} placement="right">
        <ListItemButton
          onClick={(e) => !item.disabled && onPopperToggle(e, item.children!)}
          selected={isActive}
          disabled={item.disabled}
          sx={{
            minHeight: 48,
            justifyContent: 'center',
            borderRadius: 2,
            px: 2.5,
            '&.Mui-selected': {
              backgroundColor: (theme) =>
                alpha(theme.palette.primary.main, 0.12),
              '&:hover': {
                backgroundColor: (theme) =>
                  alpha(theme.palette.primary.main, 0.18),
              },
            },
          }}
        >
          <ListItemIcon
            sx={{
              minWidth: 0,
              mr: 'auto',
              justifyContent: 'center',
            }}
          >
            {item.icon}
          </ListItemIcon>
        </ListItemButton>
      </Tooltip>
    );
  }

  // Collapsed drawer without nested items - simple navigation
  if (!drawerOpen && item.path) {
    const content = (
      <Tooltip title={item.label} placement="right">
        <ListItemButton
          selected={isActive}
          disabled={item.disabled}
          sx={{
            minHeight: 48,
            justifyContent: 'center',
            borderRadius: 2,
            px: 2.5,
            '&.Mui-selected': {
              backgroundColor: (theme) =>
                alpha(theme.palette.primary.main, 0.12),
              '&:hover': {
                backgroundColor: (theme) =>
                  alpha(theme.palette.primary.main, 0.18),
              },
            },
          }}
        >
          <ListItemIcon
            sx={{
              minWidth: 0,
              mr: 'auto',
              justifyContent: 'center',
            }}
          >
            {item.icon}
          </ListItemIcon>
        </ListItemButton>
      </Tooltip>
    );

    if (item.disabled) {
      return content;
    }

    return (
      <Link
        to={item.path}
        activeProps={{
          style: { textDecoration: 'none', color: 'inherit' },
        }}
        inactiveProps={{
          style: { textDecoration: 'none', color: 'inherit' },
        }}
      >
        {({ isActive: linkIsActive }) => (
          <Tooltip title={item.label} placement="right">
            <ListItemButton
              selected={linkIsActive}
              sx={{
                minHeight: 48,
                justifyContent: 'center',
                borderRadius: 2,
                px: 2.5,
                '&.Mui-selected': {
                  backgroundColor: (theme) =>
                    alpha(theme.palette.primary.main, 0.12),
                  '&:hover': {
                    backgroundColor: (theme) =>
                      alpha(theme.palette.primary.main, 0.18),
                  },
                },
              }}
            >
              <ListItemIcon
                sx={{
                  minWidth: 0,
                  mr: 'auto',
                  justifyContent: 'center',
                }}
              >
                {item.icon}
              </ListItemIcon>
            </ListItemButton>
          </Tooltip>
        )}
      </Link>
    );
  }

  // Expanded drawer - full display
  const listItemButton = (
    <ListItemButton
      onClick={() =>
        hasChildren && !item.disabled && onToggle(item.id, expanded)
      }
      selected={isActive}
      disabled={item.disabled}
      sx={{
        pl: depth === 0 ? 2.5 : 2.5 + depth * 2,
        borderRadius: 2,
        '&.Mui-selected': {
          backgroundColor: (theme) => alpha(theme.palette.primary.main, 0.12),
          '&:hover': {
            backgroundColor: (theme) => alpha(theme.palette.primary.main, 0.18),
          },
        },
      }}
    >
      {item.icon && (
        <ListItemIcon sx={{ minWidth: 40 }}>{item.icon}</ListItemIcon>
      )}

      <ListItemText
        primary={item.label}
        slotProps={{
          primary: {
            fontSize: depth > 0 ? '0.775rem' : '0.8375rem',
            fontWeight: depth > 0 ? 400 : 500,
          }
        }}
      />
      {hasChildren &&
        (expanded ? (
          <ExpandMoreRoundedIcon fontSize="small" />
        ) : (
          <ChevronRightRoundedIcon fontSize="small" />
        ))}
    </ListItemButton>
  );

  return (
    <React.Fragment key={item.id}>
      {item.path && !hasChildren ? (
        item.disabled ? (
          <ListItemButton
            selected={isActive}
            disabled
            sx={{
              pl: depth === 0 ? 2.5 : 2.5 + depth * 2,
              borderRadius: 2,
              '&.Mui-selected': {
                backgroundColor: (theme) =>
                  alpha(theme.palette.primary.main, 0.12),
                '&:hover': {
                  backgroundColor: (theme) =>
                    alpha(theme.palette.primary.main, 0.18),
                },
              },
            }}
          >
            {item.icon && (
              <ListItemIcon sx={{ minWidth: 40 }}>{item.icon}</ListItemIcon>
            )}
            <ListItemText
              primary={item.label}
              slotProps={{
                primary: {
                  fontSize: depth > 0 ? '0.775rem' : '0.8375rem',
                  fontWeight: depth > 0 ? 400 : 500,
                }
              }}
            />
          </ListItemButton>
        ) : (
          <Link
            to={item.path}
            activeProps={{
              style: { textDecoration: 'none', color: 'inherit' },
            }}
            inactiveProps={{
              style: { textDecoration: 'none', color: 'inherit' },
            }}
          >
            {({ isActive: linkIsActive }) => (
              <ListItemButton
                selected={linkIsActive}
                sx={{
                  pl: depth === 0 ? 2.5 : 2.5 + depth * 2,
                  borderRadius: 2,
                  '&.Mui-selected': {
                    backgroundColor: (theme) =>
                      alpha(theme.palette.primary.main, 0.12),
                    '&:hover': {
                      backgroundColor: (theme) =>
                        alpha(theme.palette.primary.main, 0.18),
                    },
                  },
                }}
              >
                {item.icon && (
                  <ListItemIcon sx={{ minWidth: 40 }}>{item.icon}</ListItemIcon>
                )}
                <ListItemText
                  primary={item.label}
                  slotProps={{
                    primary: {
                      fontSize: depth > 0 ? '0.775rem' : '0.8375rem',
                      fontWeight: depth > 0 ? 400 : 500,
                    }
                  }}
                />
              </ListItemButton>
            )}
          </Link>
        )
      ) : (
        listItemButton
      )}

      {hasChildren && (
        <Collapse in={expanded} timeout="auto" unmountOnExit>
          <List
            component="nav"
            sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}
          >
            {item.children!.map((child) => (
              <NavItem
                key={child.id}
                item={child}
                depth={depth + 1}
                drawerOpen={drawerOpen}
                isExpanded={isExpanded}
                onToggle={onToggle}
                onPopperToggle={onPopperToggle}
              />
            ))}
          </List>
        </Collapse>
      )}
    </React.Fragment>
  );
};
